import * as signalR from "@microsoft/signalr";

export let connection: signalR.HubConnection;
let startPromise: Promise<void> | null = null;

export const initializeChatHub = (token: string) => {
  if (connection) {
    if (connection.state === signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }
    if (connection.state === signalR.HubConnectionState.Connecting || connection.state === signalR.HubConnectionState.Reconnecting) {
      return startPromise || Promise.resolve();
    }
  }

  connection = new signalR.HubConnectionBuilder()
    .withUrl(`${process.env.REACT_APP_BASE_URL}/chatHub`, {
      transport: signalR.HttpTransportType.WebSockets,
      accessTokenFactory: () => (localStorage.getItem("token") || "").replace(/(^"|"$)/g, ''),
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Information)
    .build();

  startPromise = connection.start().then(() => {
    startPromise = null;
  }).catch(err => {
    startPromise = null;
    throw err;
  });

  return startPromise;
};

// Helper to ensure connection is ready before any invoke
export const ensureConnected = async () => {
  if (!connection) {
    const token = localStorage.getItem("token") || "";
    await initializeChatHub(token);
  }

  if (connection.state === signalR.HubConnectionState.Connected) {
    return;
  }

  if (startPromise) {
    await startPromise;
  } else if (connection.state === signalR.HubConnectionState.Disconnected) {
    const token = localStorage.getItem("token") || "";
    await initializeChatHub(token);
  }
};

// Use this to ensure connection is active without re-initializing if unnecessary
export const ensureGlobalConnection = async (token: string) => {
  if (!connection || connection.state === signalR.HubConnectionState.Disconnected) {
    return await initializeChatHub(token);
  }
};


export const joinChatRoom = async (caseId: string) => {
  await ensureConnected();
  await connection.invoke("JoinCaseChat", caseId);
};

export const leaveChatRoom = async (caseId: string) => {
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("LeaveCaseChat", caseId);
    } catch (err) {
      console.warn("Failed to leave chat room:", err);
    }
  }
};

export const sendMessage = async (
  caseId: number | null,
  senderUserId: number | null,
  content: string,
  type: string = "text",
  attachmentUrl: string | null = "",
  fileName: string | null = ""
) => {
  await ensureConnected();
  await connection.invoke(
    "SendMessage",
    caseId,
    senderUserId,
    content,
    type,
    attachmentUrl,
    fileName
  );
};

let messageHandler: ((message: any) => void) | null = null;

export const onReceiveMessage = (handler: (message: any) => void) => {
  if (connection) {
    if (messageHandler) {
      connection.off("ReceiveMessage", messageHandler);
    }
    messageHandler = handler;
    connection.on("ReceiveMessage", handler);
  }
};

export const disconnectChatHub = async () => {
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.stop();
    } catch (err) {
      console.warn("Error while disconnecting SignalR:", err);
    }
  } else {

    // console.log("SignalR was already disconnected or not initialized.");
  }
};

// Add these to your existing signalRService.ts
// export const sendSOSAlert = async (caseId: string, senderId: number) => {
//   if (connection) {
//     // In your React handleSOS function
// const numericCaseId = parseInt(caseId);
// const numericUserId = parseInt(senderId);
//     // Calling the new .NET method
//     await connection.invoke("TriggerSOS", numericCaseId, numericUserId);
//   }
// };

export const sendSOSAlert = async (caseId: any, senderId: any) => {
  if (connection) {
    // Force conversion to numbers and validate
    const nCaseId = Number(caseId);
    const nUserId = Number(senderId);

    // Check if the conversion was successful
    if (isNaN(nCaseId) || isNaN(nUserId)) {
      console.error("SOS Failed: Invalid IDs", { caseId, senderId });
      return;
    }

    // Call the .NET method
    try {
      await ensureConnected();
      await connection.invoke("TriggerSOS", nCaseId, nUserId);
    } catch (err) {
      console.error("SignalR TriggerSOS Error:", err);
      throw err;
    }
  }
};

export const onReceiveSOS = (callback: (data: any) => void) => {
  if (connection) {
    connection.on("AdminNotified", callback);
  }
};

// Global listener for new messages (for notifications)
export const onReceiveGlobalMessage = (handler: (message: any) => void) => {
  if (!connection) return;

  const attachListener = () => {
    connection?.off("ReceiveMessageGlobal"); // Unique event name for background alerts
    connection?.on("ReceiveMessageGlobal", handler);
  };

  if (connection.state === signalR.HubConnectionState.Connected) {
    attachListener();
  } else {
    connection.onreconnected(attachListener);
  }
};

// signalRService.tsx
export const onReceiveGlobalSOS = (handler: (data: any) => void) => {
  if (!connection) return;

  const attachListener = () => {
    connection.off("ReceiveGlobalAdminAlert"); // Prevent duplicates
    connection.on("ReceiveGlobalAdminAlert", (data) => {
      handler(data);
    });
  };

  if (connection.state === signalR.HubConnectionState.Connected) {
    attachListener();
  } else {
    // This covers both initial start and reconnections
    connection.onreconnected(attachListener);
    // Extra safety: attach once connection is fully established
    const checkInterval = setInterval(() => {
      if (connection?.state === signalR.HubConnectionState.Connected) {
        attachListener();
        clearInterval(checkInterval);
      }
    }, 1000);
  }
};

// Cleanup method for global alerts
export const offReceiveGlobalSOS = () => {
  if (connection) {
    connection.off("ReceiveGlobalAdminAlert");
  }
};

export const onReceiveNotification = (handler: (data: any) => void) => {
  if (!connection) return;

  const attachListener = () => {
    connection.off("ReceiveNotification");
    connection.on("ReceiveNotification", handler);
  };

  if (connection.state === signalR.HubConnectionState.Connected) {
    attachListener();
  } else {
    connection.onreconnected(attachListener);
  }
};

export const offReceiveNotification = () => {
  if (connection) {
    connection.off("ReceiveNotification");
  }
};

// --- Read Receipts ---
export const markAsRead = async (caseId: number, userId: number) => {
  await ensureConnected();
  if (connection && connection.state === signalR.HubConnectionState.Connected) {
    try {
      await connection.invoke("MarkAsRead", caseId, userId);
    } catch (err) {
      console.error("Failed to invoke MarkAsRead", err);
    }
  }
};

export const onReceiveReadReceipt = (handler: (data: any) => void) => {
  if (connection) {
    connection.off("MessagesRead"); // Prevent duplicates
    connection.on("MessagesRead", handler);
  }
};

export const offReceiveReadReceipt = () => {
  if (connection) {
    connection.off("MessagesRead");
  }
};

export const onReceiveCallInvite = (handler: (data: any) => void) => {
  if (connection) {
    connection.on("IncomingCall", handler);
  }
};

export const offReceiveCallInvite = () => {
  if (connection) {
    connection.off("IncomingCall");
  }
};

export const onReceiveCallEnded = (handler: (data: any) => void) => {
  if (connection) {
    connection.on("CallEnded", handler);
  }
};

export const offReceiveCallEnded = () => {
  if (connection) {
    connection.off("CallEnded");
  }
};

